import { z } from 'zod'

// Stops filter enum
export const stopsFilterSchema = z.enum(['all', 'direct', 'one_stop_max', 'two_stops_max'])
export type StopsFilterValue = z.infer<typeof stopsFilterSchema>

// Luggage filter enum (minimum checked bags required)
export const luggageFilterSchema = z.enum(['none', '0', '1', '2'])
export type LuggageFilterValue = z.infer<typeof luggageFilterSchema>

/**
 * Filtre « Type de paiement ». Repose sur `isInstallmentEligibleAtSearch`, le
 * même drapeau que la mention de la carte : `massilia` = plusieurs fois sans
 * frais Massilia, `partner` = 1 fois ou plusieurs fois avec frais (Oney/FLOA).
 */
export const paymentFilterSchema = z.enum(['all', 'massilia', 'partner'])
export type PaymentFilterValue = z.infer<typeof paymentFilterSchema>

// Sort filter enum
const sortFilterSchema = z.enum(['price-asc', 'price-desc'])
export type SortFilterValue = z.infer<typeof sortFilterSchema>

// Flight results search params schema - directly embeds NewFlightSchema as JSON
export const flightResultsSearchSchema = z.object({
  // Filter state
  filterAirlines: z.string().optional(),
  filterDurationMax: z.coerce.number().optional(),
  filterDurationMin: z.coerce.number().optional(),
  filterLuggage: luggageFilterSchema.optional().default('0'),
  filterPayment: paymentFilterSchema.optional().default('all'),
  filterPriceMax: z.coerce.number().optional(),
  filterPriceMin: z.coerce.number().optional(),
  filterSort: sortFilterSchema.optional(),
  filterStops: stopsFilterSchema.optional().default('all'),
  filterTimeRanges: z.string().optional(),
  // Search criteria - parsed automatically by TanStack Router from JSON
  flightSearch: z.record(z.unknown()),
  // Marqueur posé par « relancer la recherche » (cf. `useRestartFlightSearch`) : sa
  // seule valeur est d'être différent à chaque clic. Il casse la queryKey react-query
  // et déclenche le contournement du cache serveur.
  refresh: z.coerce.number().optional(),
})

/**
 * Search params de /vols-multi/resultats. Les mêmes filtres que la page mono,
 * mais la recherche elle-même arrive en `search` (StructuredMultiSearchRequest
 * sérialisée) au lieu de `flightSearch`.
 */
export const multiFlightResultsSearchSchema = z.object({
  filterAirlines: z.string().optional(),
  filterDurationMax: z.coerce.number().optional(),
  filterDurationMin: z.coerce.number().optional(),
  filterLuggage: luggageFilterSchema.optional().default('0'),
  filterPayment: paymentFilterSchema.optional().default('all'),
  filterPriceMax: z.coerce.number().optional(),
  filterPriceMin: z.coerce.number().optional(),
  filterStops: stopsFilterSchema.optional().default('all'),
  filterTimeRanges: z.string().optional(),
  search: z.unknown().optional(),
})

/**
 * Critères d'une page résultats, quelle que soit la page : c'est ce que mémorise
 * `useFlightSearchStore` pour pouvoir rejouer la recherche depuis le tunnel.
 */
export type FlightResultsPageSearch = FlightResultsSearch | MultiFlightResultsSearch

export type FlightResultsSearch = z.infer<typeof flightResultsSearchSchema>

export type MultiFlightResultsSearch = z.infer<typeof multiFlightResultsSearchSchema>
